import Phaser from 'phaser';
import { searchUsernames } from '../../store/friends.js';
import { sendInvite } from '../../store/invites.js';
import { Button } from './Button.js';
import { TextInputOverlay } from './TextInputOverlay.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Invite-by-username modal — host enters a username, optionally
 * picks a typeahead suggestion, taps Send → POSTs to
 * /api/games/:id/invite. On success, the realtime UPDATE on
 * game_seats reaches JoinScene which re-renders the held seat row.
 *
 * Mirrors ChangeEmailModal's state-machine pattern:
 *   ready  → typing
 *   working → request in flight
 *   success → invite landed; auto-dismisses after 1.2s
 *   error  → server rejected; show error + retry button
 *
 * Typeahead reuses friends-store's searchUsernames (the same prefix
 * search the FriendsScene ADD tab uses). Up to 5 results, debounced
 * 250ms. Tapping a result fills the input + immediately enables
 * Send.
 *
 * Slice 1.8 of v1.0/invites.
 */

const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 560;
const PANEL_HEIGHT = 560;
const PANEL_RADIUS = 12;

const TYPEAHEAD_DEBOUNCE_MS = 250;
const TYPEAHEAD_ROW_HEIGHT = 48;
const TYPEAHEAD_MAX_ROWS = 5;

type State = 'ready' | 'working' | 'success' | 'error';

export interface InviteUsernameModalOptions {
  readonly gameId: string;
  readonly onCancel: () => void;
  /** Called once the invite has been confirmed by the server.
   *  The modal handles its own dismiss-animation; this callback
   *  fires when the modal is fully destroyed. */
  readonly onSuccess: (invitedUsername: string) => void;
}

export class InviteUsernameModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: InviteUsernameModalOptions;

  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private bodyObjects: Phaser.GameObjects.GameObject[] = [];
  private typeaheadObjects: Phaser.GameObjects.GameObject[] = [];
  private buttons: Button[] = [];
  private input: TextInputOverlay | null = null;

  private currentState: State = 'ready';
  private typedValue = '';
  private typeaheadResults: ReadonlyArray<{
    userId: string;
    username: string;
  }> = [];
  private searchTimer: number | null = null;
  private latestSearchId = 0;
  private errorMsg = '';
  private successUsername = '';
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: InviteUsernameModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.drawBackdrop();
    this.drawPanel();
    this.renderBody();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });
  }

  // --- Chrome ---

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      .setInteractive();
    // No backdrop-tap cancel — input overlay sits over this and a
    // stray tap during typing would close the modal. Cancel button
    // is the only dismiss path.
    this.objects.push(bg);
  }

  private drawPanel(): void {
    const left = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    panel.lineStyle(2, COLORS.accentCyanInt, 0.95);
    panel.strokeRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    this.objects.push(panel);
  }

  // --- State-driven body ---

  private renderBody(): void {
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    for (const o of this.typeaheadObjects) o.destroy();
    this.typeaheadObjects = [];
    this.input?.destroy();
    this.input = null;
    for (const b of this.buttons) b.destroy();
    this.buttons = [];

    switch (this.currentState) {
      case 'ready':
        this.renderReady();
        break;
      case 'working':
        this.renderWorking();
        break;
      case 'success':
        this.renderSuccess();
        break;
      case 'error':
        this.renderError();
        break;
    }
  }

  private renderReady(): void {
    const cx = DESIGN_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    this.addTitle('Invite by username', top + 36, COLORS.accentCyanHex);
    this.addBody(
      "Send a private invite to anyone with a BattleShark account. They'll see the invite in their inbox and can accept on their own time.",
      top + 110,
      COLORS.textSecondary,
      'left'
    );

    this.input = new TextInputOverlay({
      scene: this.scene,
      x: cx,
      y: top + 250,
      width: 420,
      height: 64,
      type: 'text',
      placeholder: 'username',
      autocomplete: 'off',
      fontSizeDesign: 22,
      onChange: (v) => {
        this.typedValue = v;
        this.scheduleSearch(v);
        this.refreshSubmitEnabled();
      },
      onSubmit: () => {
        if (this.canSubmit()) void this.handleSubmit();
      },
    });

    this.renderTypeaheadRows();

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    this.addSubmitButton(top + PANEL_HEIGHT - 56, cx + 130);
    this.refreshSubmitEnabled();
  }

  private renderTypeaheadRows(): void {
    for (const o of this.typeaheadObjects) o.destroy();
    this.typeaheadObjects = [];

    if (this.typeaheadResults.length === 0) return;

    const cx = DESIGN_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const firstRowY = top + 312;

    for (
      let i = 0;
      i < Math.min(this.typeaheadResults.length, TYPEAHEAD_MAX_ROWS);
      i++
    ) {
      const entry = this.typeaheadResults[i];
      if (!entry) continue;
      const rowY = firstRowY + i * TYPEAHEAD_ROW_HEIGHT;

      const rowBg = this.scene.add
        .rectangle(cx, rowY, 420, TYPEAHEAD_ROW_HEIGHT - 2, COLORS.bgPanelInt, 1)
        .setStrokeStyle(1, COLORS.borderSubtleInt, 1)
        .setDepth(PANEL_DEPTH + 1)
        .setInteractive({ useHandCursor: true });
      rowBg.on('pointerup', () => {
        this.typedValue = entry.username;
        this.input?.setValue(entry.username);
        this.typeaheadResults = [];
        this.renderTypeaheadRows();
        this.refreshSubmitEnabled();
      });
      this.typeaheadObjects.push(rowBg);

      const rowText = this.scene.add
        .text(cx, rowY, entry.username, {
          fontFamily: FONT_MONO,
          fontSize: '22px',
          color: COLORS.textPrimary,
        })
        .setOrigin(0.5)
        .setDepth(PANEL_DEPTH + 2);
      this.typeaheadObjects.push(rowText);
    }
  }

  private renderWorking(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    this.addTitle('Sending invite…', top + 36, COLORS.accentCyanHex);
    this.addBody(
      `Inviting ${this.typedValue}…`,
      top + 200,
      COLORS.textMuted
    );
  }

  private renderSuccess(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    this.addTitle('Invite sent', top + 36, COLORS.accentCyanHex);
    this.addBody(
      `${this.successUsername} is invited. Their seat is held until they accept.`,
      top + 200,
      COLORS.textPrimary,
      'left'
    );
    // Auto-dismiss after a short read.
    this.scene.time.delayedCall(1200, () => {
      this.opts.onSuccess(this.successUsername);
      this.destroy();
    });
  }

  private renderError(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const cx = DESIGN_WIDTH / 2;

    this.addTitle("Couldn't send invite", top + 36, COLORS.accentRedHex);
    this.addBody(this.errorMsg, top + 140, COLORS.textPrimary, 'left');

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    const retry = new Button(this.scene, {
      x: cx + 130,
      y: top + PANEL_HEIGHT - 56,
      width: 220,
      height: 64,
      text: 'Try again',
      style: 'primary',
      onTap: () => {
        this.currentState = 'ready';
        this.renderBody();
      },
    });
    retry.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(retry);
  }

  // --- Helpers ---

  private addTitle(text: string, y: number, color: string): void {
    const cx = DESIGN_WIDTH / 2;
    const t = this.scene.add
      .text(cx, y, text.toUpperCase(), {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color,
        fontStyle: '700',
      })
      .setOrigin(0.5, 0)
      .setLetterSpacing(3)
      .setDepth(PANEL_DEPTH + 2);
    this.bodyObjects.push(t);
  }

  private addBody(
    text: string,
    y: number,
    color: string,
    align: 'center' | 'left' = 'center'
  ): void {
    const cx = DESIGN_WIDTH / 2;
    const x = align === 'left' ? cx - PANEL_WIDTH / 2 + 36 : cx;
    const t = this.scene.add
      .text(x, y, text, {
        fontFamily: FONT_MONO,
        fontSize: '18px',
        color,
        align,
        wordWrap: { width: PANEL_WIDTH - 72 },
      })
      .setOrigin(align === 'left' ? 0 : 0.5, 0)
      .setDepth(PANEL_DEPTH + 2);
    this.bodyObjects.push(t);
  }

  private addCancelButton(y: number, x: number): void {
    const b = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Cancel',
      style: 'default',
      onTap: () => {
        this.opts.onCancel();
        this.destroy();
      },
    });
    b.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(b);
  }

  private addSubmitButton(y: number, x: number): void {
    const b = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Send invite',
      style: 'primary',
      onTap: () => {
        if (this.canSubmit()) void this.handleSubmit();
      },
    });
    b.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(b);
  }

  private canSubmit(): boolean {
    return this.typedValue.trim().length > 0;
  }

  private refreshSubmitEnabled(): void {
    // Submit button is the last in the buttons array (index 1 of 2).
    const submit = this.buttons[1];
    if (submit) submit.setEnabled(this.canSubmit());
  }

  private scheduleSearch(query: string): void {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      this.typeaheadResults = [];
      this.renderTypeaheadRows();
      return;
    }
    const id = ++this.latestSearchId;
    this.searchTimer = window.setTimeout(() => {
      void this.runSearch(trimmed, id);
    }, TYPEAHEAD_DEBOUNCE_MS);
  }

  private async runSearch(query: string, id: number): Promise<void> {
    const resp = await searchUsernames(query);
    if (this.destroyed) return;
    if (id !== this.latestSearchId) return;
    this.typeaheadResults = resp?.results ?? [];
    this.renderTypeaheadRows();
  }

  private async handleSubmit(): Promise<void> {
    this.currentState = 'working';
    this.renderBody();

    const result = await sendInvite(this.opts.gameId, this.typedValue.trim());
    if (this.destroyed) return;

    if (result.ok) {
      this.successUsername = result.invitedUsername;
      this.currentState = 'success';
    } else {
      this.errorMsg = this.friendlyError(result.error, result.status);
      this.currentState = 'error';
    }
    this.renderBody();
  }

  private friendlyError(serverError: string, status: number): string {
    // Map common server responses to player-friendly copy. Fallback
    // to the raw error when we don't have a mapping.
    if (status === 404 && /username not found/i.test(serverError)) {
      return "No player with that username. Double-check the spelling.";
    }
    if (status === 409 && /already in this game/i.test(serverError)) {
      return 'That player is already in this game.';
    }
    if (status === 409 && /no open seats/i.test(serverError)) {
      return 'This game has no open seats left.';
    }
    if (status === 409 && /game already started/i.test(serverError)) {
      return 'This game has already started — invites are closed.';
    }
    if (status === 429) {
      return 'Slow down — too many invites this hour. Try again later.';
    }
    if (status === 403) {
      return 'Only the host can send invites.';
    }
    if (status === 400 && /yourself/i.test(serverError)) {
      return "You can't invite yourself.";
    }
    return serverError || 'Something went wrong. Try again?';
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    for (const o of this.objects) o.destroy();
    for (const o of this.bodyObjects) o.destroy();
    for (const o of this.typeaheadObjects) o.destroy();
    for (const b of this.buttons) b.destroy();
    this.input?.destroy();
  }
}
